import React, { useEffect, useMemo, useState } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";

import { useNotification } from "@hooks/useNotification";
import { useReduxSelector } from "@hooks/useReduxSelector";
import "date-fns";
import { FormProvider, Controller } from "react-hook-form";

// import IconInfo from "@image/icones/DocumentsEmployers/iconInfo.svg";
import "@responsivity/responsivityMobile/Employer/parcoursCertifications.css";

// import AddButton from "@components/buttons/AddButton";
import FormTextarea from "@components/formGroup/formTextarea";
import { Input, InputGroup } from "@components/tailwindComponents";
// import { Switch } from "@components/tailwindComponents";
import Button from "@components/tailwindComponents/Button";
import ListboxSimple from "@components/tailwindComponents/ListboxSimple";

// import CustomTooltip from "@components/tooltip/index";
import {
  checkErrors,
  mapEmplacementToDevise,
  parseNumberToFloat
} from "@helpers/_functions";
import { SalaryAndAdvantage } from "@services/all.services";

import ModalDeleteSalary from "../../modals/ModalDeleteSalary";
import AdvantageList from "./AdvantageList";
import SalaryInjection from "./SalaryInjection";
// import TextAreaNote from "../../modals/ModalSalaryForm/subComponents/TextAreaNote";
import DropdownTypeOfEmployment from "./subComponents/DropdownTypeOfEmployment";
// import InputAdvantageTitle from "../../modals/ModalSalaryForm/subComponents/InputAdvantageTitle";
// import InputAnnualAdvantage from "../../modals/ModalSalaryForm/subComponents/InputAnnualAdvantage";
import InputAnnualSalary from "./subComponents/InputAnnualSalary";
import InputHourlyRate from "./subComponents/InputHourlyRate";
import InputHoursPerWeek from "./subComponents/InputHoursPerWeek";
import PeriodDatePickers from "./subComponents/PeriodDatePickers";
import SecondaryEmploymentSwitch from "./subComponents/SecondaryEmploymentSwitch";
import { useSalaryForm } from "./useSalaryForm";

const DATE_FORMAT = "YYYY-MM-DD";

/**
 *
 * @param {Object} props
 * @param {Object} props.career
 * @param {boolean} props.readOnly
 * @param {any[]} props.careerList
 * @param {Object | null} props.employeePoste
 * @param {Object} props.selectedSalary
 * @param {boolean} props.isOpen
 * @param {string} props.employeeId
 * @param {any[]} props.otherSalaries
 * @param {() => void=} props.onClose
 * @returns
 */
export default function SalaryDetailsForm(props) {
  const { t: translate } = useTranslation(
    "pages.employés.parcours&Certifications"
  );

  const { showError, showSuccess } = useNotification();

  const emplacementsList = useReduxSelector(
    ({ commonData }) => commonData.emplacements
  );
  const deviseList = useMemo(
    () => mapEmplacementToDevise(emplacementsList),
    [emplacementsList]
  );

  const { salaryForm, resetForm, populateForm } = useSalaryForm({
    career: props.career,
    careerList: props.careerList,
    deviseList
  });

  const { control, setValue } = salaryForm;

  useEffect(() => {
    if (props.isOpen) {
      if (props.selectedSalary) {
        populateForm(props.selectedSalary);
      } else {
        resetForm();
      }

      if (props.career?.status) {
        setValue("salaryEnd", "");

        if (props.career.dateStart) {
          setValue(
            "salaryBegin",
            moment(props.career.dateStart, DATE_FORMAT).toDate(),
            { shouldValidate: true }
          );
        }
      }
    }
  }, [props.isOpen, props.selectedSalary]);

  const [isLoading, setLoading] = useState(false);
  async function onSaveClicked(formValue) {
    try {
      setLoading(true);
      let salaryEnd = "";
      if (!formValue.isInProgress && !props.career?.status) {
        salaryEnd = moment(formValue.salaryEnd).format(DATE_FORMAT);
      }

      const salaryBegin = moment(formValue.salaryBegin).format(DATE_FORMAT);

      const advantageTypes =
        formValue.AdvantageType?.map((adv) => ({
          _id: adv._id || null,
          name: adv.name || "",
          annualValue: adv.annualValue || "",
          annualDevise: adv.annualDevise?._id || "",
          note: adv.note || ""
        })) || [];

      const salaryData = {
        ...formValue,
        salaryBegin,
        salaryEnd,
        jobType: formValue.jobType?.value,
        careerId: formValue.career?._id,
        additionalInformation: formValue.additionalInformation,
        Enterprise: localStorage.getItem("enterpriseId"),
        Employer: props.employeeId,
        AdvantageType: advantageTypes
      };

      let response = null;
      if (props.selectedSalary) {
        response = await SalaryAndAdvantage.updateById(salaryData);
      } else {
        response = await SalaryAndAdvantage.insert(salaryData);
      }

      if (!response?.data?.success) {
        showError(response?.data?.message, { date: response?.data?.date });
        throw new Error(
          `Error on ${props.selectedSalary ? "updating" : "adding"} salary`
        );
      }

      let message = props.selectedSalary
        ? "update_action_success"
        : "creation_action_success";
      showSuccess(message, { object: "salary" });

      if (props.onClose) {
        props.onClose();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  function handleError(errors) {
    const errorArray = Object.values(errors);
    if (checkErrors(errorArray, "annualSalaryMax")) {
      showError(translate("annual_salary_max_exceeded", { ns: "message" }));
      return;
    }

    if (checkErrors(errorArray, "required")) {
      showError("field_obligatory");
      return;
    }

    if (checkErrors(errorArray, "isBetweenPeriod")) {
      showError("La période sélectionnée dépasse celle du poste approprié");
      return;
    }
    if (checkErrors(errorArray, "isAfterSalaryBegin")) {
      showError("verification_two_object", {
        object1: "endDate",
        operator: "superiour",
        object2: "startDate"
      });
      return;
    }

    if (checkErrors(errorArray, "noOverlap")) {
      showError("superposition");
      return;
    }

    if (errorArray[0] && checkErrors([errorArray[0]], "isAdvantageTypeValid")) {
      showError("field_obligatory");
      return;
    }
  }

  const [modalDeleteSalary, setModalDeleteSalary] = useState(false);
  function handleDeleteClicked() {
    setModalDeleteSalary(!modalDeleteSalary);
  }

  return (
    <FormProvider {...salaryForm}>
      {!props.readOnly && !props.selectedSalary && props.employeePoste && (
        <SalaryInjection
          employeePoste={props.employeePoste}
          populateForm={populateForm}
        />
      )}

      <div className="arh-grid arh-grid-cols-3 arh-gap-x-10 arh-gap-y-4">
        <div className="arh-flex arh-flex-col arh-gap-0">
          <Controller
            control={control}
            name="title"
            rules={{ required: true }}
            render={({
              field: { onChange, value, name },
              fieldState: { invalid }
            }) => (
              <div className="arh-mb-3">
                <Input
                  name={name}
                  onChange={onChange}
                  value={value}
                  invalid={invalid}
                  disabled={props.readOnly}
                  placeholder={translate("Échelon/Titre du parcours")}
                  label={translate("Échelon/Titre du parcours") + " *"}
                />
              </div>
            )}
          />

          <Controller
            control={control}
            rules={{ required: true }}
            name="career"
            render={({
              field: { onChange, value: selectedCareer, name },
              fieldState: { invalid }
            }) => (
              <div className="blackSvgColor position_relative arh-mb-3">
                <ListboxSimple
                  name={name}
                  placeholder={translate("Nom du poste")}
                  optionList={props.careerList}
                  optionTextAttribut="title"
                  selectedValue={selectedCareer}
                  setSelectedValue={onChange}
                  label={translate("Relié au poste") + " *"}
                  invalid={invalid}
                  noRadio
                  withSearch
                  readOnly
                />
              </div>
            )}
          />

          <div>
            <PeriodDatePickers
              readOnly={props.readOnly}
              career={props.career}
              otherSalaries={props.otherSalaries}
            />
          </div>

          <DropdownTypeOfEmployment readOnly={props.readOnly} />
          <SecondaryEmploymentSwitch
            control={control}
            readOnly={props.readOnly}
            translate={translate}
          />
        </div>

        <div className="arh-flex arh-flex-col arh-gap-0">
          <InputAnnualSalary readOnly={props.readOnly} />
          <InputHourlyRate readOnly={props.readOnly} />
          <InputHoursPerWeek readOnly={props.readOnly} />

          <Controller
            control={control}
            name="vacancyPercentage"
            defaultValue={""}
            render={({ field: { onChange, value, name } }) => (
              <div className="arh-mb-3">
                <InputGroup
                  name={name}
                  value={value || ""}
                  onChange={(event) => {
                    const nextValue = parseNumberToFloat(event.target.value);
                    onChange(nextValue);
                  }}
                  disabled={props.readOnly}
                  placeholder={translate("Pourcentage de vacance")}
                  label={translate("Pourcentage de vacance")}
                  classParent="arh-w-full"
                  content="%"
                />
              </div>
            )}
          />
        </div>

        <div className="arh-flex arh-flex-col">
          <div className="arh-mb-0">
            <Controller
              control={control}
              name="additionalInformation"
              render={({ field: { onChange, value, name } }) => (
                <FormTextarea
                  placeholder={translate(
                    "Paiement additionnel / Stock Options"
                  )}
                  classTextarea="arh-min-h-[295px]"
                  label={translate("Informations additionnelles")}
                  name={name}
                  value={value}
                  onChange={onChange}
                  readOnly={props.readOnly}
                />
              )}
            />
          </div>
        </div>
      </div>

      <AdvantageList
        readOnly={props.readOnly}
        deviseList={deviseList}
        handleError={handleError}
      />

      {!props.readOnly && (
        <div
          className={`arh-mt-4 arh-flex arh-w-full arh-items-center arh-justify-start arh-space-x-4 mobile-768:arh-flex-col mobile-768:arh-space-x-0 mobile-768:arh-space-y-4`}
        >
          <Button
            variant="outlined"
            onClick={() =>
              !props.selectedSalary ? props.onClose() : handleDeleteClicked()
            }
            className="!arh-w-auto arh-min-w-[7.625rem] arh-max-w-[7.625rem] mobile-768:!arh-w-full mobile-768:arh-max-w-full"
          >
            {!props.selectedSalary ? translate("Annuler") : translate("Effacer")}
          </Button>
          <Button
            variant="green"
            onClick={salaryForm.handleSubmit(onSaveClicked, handleError)}
            className="!arh-w-auto arh-min-w-[8.25rem] arh-max-w-[8.25rem] mobile-768:!arh-w-full mobile-768:arh-max-w-full"
            disabled={isLoading}
            wait={isLoading}
          >
            {translate("Enregistrer")}
          </Button>
        </div>
      )}
      <ModalDeleteSalary
        onClickClose={handleDeleteClicked}
        salaryId={props.selectedSalary?._id}
        isOpen={modalDeleteSalary}
        toggle={handleDeleteClicked}
      />
    </FormProvider>
  );
}
